import { useState, useEffect, useCallback, useMemo } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "./CalendarioPlanner.css";
import axios from "axios";
import { toast } from "react-toastify";
import { format, toZonedTime } from "date-fns-tz";

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://interventi.synology.me:5050";

const Calendario = ({
  interventi = [],
  fetchInterventi = () => {},
  date = new Date(),
  setDate = () => {},
  openModal = () => {},
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [cliente, setCliente] = useState("");
  const [descrizione, setDescrizione] = useState("");
  const [stato, setStato] = useState("Da fare");
  const [modificaId, setModificaId] = useState(null);
  const [googleEvents, setGoogleEvents] = useState([]);
  const [isGoogleConnected, setIsGoogleConnected] = useState(false);
  const [selectedDayInterventi, setSelectedDayInterventi] = useState([]);
  const [showInterventiList, setShowInterventiList] = useState(false);
  const GOOGLE_CALENDAR_ENABLED = false;

  useEffect(() => {
    const checkGoogleStatus = async () => {
      if (!GOOGLE_CALENDAR_ENABLED) {
        setIsGoogleConnected(false);
        setGoogleEvents([]);
        return;
      }
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_BASE_URL}/google-auth/status`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });
        console.log("ðŸ“… Stato autenticazione Google:", response.data);
        setIsGoogleConnected(response.data.isAuthenticated);
        if (!response.data.isAuthenticated) {
          toast.warn("Autenticazione con Google Calendar non trovata. Collega il tuo account.");
        }
      } catch (error) {
        console.error("âŒ Errore nel controllare lo stato di Google:", error);
        toast.error("Errore nel controllare lo stato di Google: " + (error.message || "Errore sconosciuto"));
        setIsGoogleConnected(false);
      }
    };
    checkGoogleStatus();
  }, []);

  const fetchGoogleEvents = async () => {
    if (!GOOGLE_CALENDAR_ENABLED) {
      setGoogleEvents([]);
      return;
    }
    try {
      const now = new Date();
      const timeMin = new Date(now.getFullYear() - 2, 0, 1).toISOString();
      const timeMax = new Date(now.getFullYear() + 2, 11, 31, 23, 59, 59).toISOString();
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/google-calendar/events`, {
        params: { timeMin, timeMax },
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      const events = response.data;
      console.log("ðŸ“… Eventi Google ricevuti dal backend (totale):", events.length);
      setGoogleEvents(events);
      if (!events || events.length === 0) {
        toast.warn("Nessun evento trovato su Google Calendar.");
      }
    } catch (error) {
      console.error("âŒ Errore nel recupero degli eventi Google:", error);
      setGoogleEvents([]);
      toast.error("Errore nella sincronizzazione con Google Calendar");
    }
  };

  useEffect(() => {
    fetchGoogleEvents();
    const interval = setInterval(fetchGoogleEvents, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isGoogleConnected) {
      fetchGoogleEvents();
    }
  }, [isGoogleConnected]);

  const handleGoogleAuth = () => {
    window.location.href = `${API_BASE_URL}/google-auth/login`;
  };

  const handleRetrySync = async () => {
    setIsGoogleConnected(false);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/google-auth/status`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      if (response.data.isAuthenticated) {
        setIsGoogleConnected(true);
        fetchGoogleEvents();
        fetchInterventi();
      } else {
        toast.warn("Autenticazione con Google Calendar non trovata. Collega il tuo account.");
      }
    } catch (error) {
      console.error("âŒ Errore durante il retry della sincronizzazione:", error);
      toast.error("Errore durante il retry della sincronizzazione!");
    }
  };

  const handleRefreshGoogleEvents = async () => {
    try {
      await fetchGoogleEvents();
      await fetchInterventi();
      toast.success("Eventi Google Calendar aggiornati!");
    } catch (error) {
      toast.error("Errore durante l'aggiornamento degli eventi Google!");
    }
  };

  const normalizeDate = (date) => {
    const d = new Date(date);
    const zonedDate = toZonedTime(d, "Europe/Rome");
    return format(zonedDate, "yyyy-MM-dd");
  };

  const eventiByDate = useMemo(() => {
    const map = new Map();
    [...interventi, ...googleEvents].forEach((e) => {
      const day = normalizeDate(e.data);
      if (!map.has(day)) {
        map.set(day, []);
      }
      map.get(day).push(e);
    });
    return map;
  }, [interventi, googleEvents]);

  const tileContent = useCallback(
    ({ date, view }) => {
      if (view !== "month") return null;
      const dateString = normalizeDate(date);
      const eventiDelGiorno = eventiByDate.get(dateString);
      if (!eventiDelGiorno || eventiDelGiorno.length === 0) return null;

      return (
        <div className="event-indicator">
          {eventiDelGiorno.length === 1 ? (
            <span className="dot"></span>
          ) : (
            <span className="event-count">{eventiDelGiorno.length}</span>
          )}
        </div>
      );
    },
    [eventiByDate]
  );

  const handleDateClick = (selectedDate) => {
    setDate(selectedDate);
    const selectedDateString = normalizeDate(selectedDate);
    const eventsInDay = eventiByDate.get(selectedDateString) || [];

    if (eventsInDay.length > 0) {
      setSelectedDayInterventi(eventsInDay);
      setShowInterventiList(true);
    } else {
      openModal("create");
      setIsModalOpen(true);
    }
  };

  const handleInterventoClick = (intervento) => {
    if (!intervento || !intervento.id) {
      console.error("âŒ Intervento non valido o ID mancante!");
      return;
    }
    setDate(new Date(intervento.data));
    setShowInterventiList(false);

    if (intervento.id.startsWith("google-")) {
      console.log("â„¹ï¸ Evento di Google Calendar, modifica bloccata.");
      toast.info("Questo è un evento di Google Calendar. Non può essere modificato qui.");
      if (intervento.htmlLink) {
        window.open(intervento.htmlLink, "_blank");
      }
    } else {
      openModal("edit", intervento);
    }
  };

  const handleAddNewIntervento = () => {
    setCliente("");
    setDescrizione("");
    setStato("Da fare");
    setModificaId(null);
    openModal("create");
    setShowInterventiList(false);
  };

  const getColorByStato = (stato) => {
    switch (stato) {
      case "Da fare":
        return "red";
      case "In corso":
        return "orange";
      case "Completato":
        return "green";
      default:
        return "gray";
    }
  };


  const plannerDays = useMemo(() => {
    const current = new Date(date || new Date());
    const year = current.getFullYear();
    const month = current.getMonth();

    const firstDay = new Date(year, month, 1);
    const startOffset = (firstDay.getDay() + 6) % 7;
    const gridStart = new Date(year, month, 1 - startOffset);

    return Array.from({ length: 42 }, (_, index) => {
      const day = new Date(gridStart);
      day.setDate(gridStart.getDate() + index);

      const key = normalizeDate(day);

      return {
        date: day,
        key,
        currentMonth: day.getMonth() === month,
        today: normalizeDate(day) === normalizeDate(new Date()),
        selected: normalizeDate(day) === normalizeDate(current),
        events: eventiByDate.get(key) || [],
      };
    });
  }, [date, eventiByDate]);

  const plannerMonthLabel = new Intl.DateTimeFormat("it-IT", {
    month: "long",
    year: "numeric",
  }).format(new Date(date || new Date()));

  const goToPreviousMonth = () => {
    const current = new Date(date || new Date());
    setDate(new Date(current.getFullYear(), current.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    const current = new Date(date || new Date());
    setDate(new Date(current.getFullYear(), current.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setDate(new Date());
  };
  return (
    <div className="planner-root">
      {GOOGLE_CALENDAR_ENABLED && (!isGoogleConnected ? (
        <button onClick={handleGoogleAuth}>Collega Google Calendar</button>
      ) : (
        <div className="google-sync-buttons">
          <button onClick={handleRetrySync}>Riprova Sincronizzazione Google Calendar</button>
          <button onClick={handleRefreshGoogleEvents}>Aggiorna Eventi Google</button>
        </div>
      ))}
      <div className="planner-calendar">
        <div className="planner-toolbar">
          <div className="planner-toolbar-left">
            <button type="button" className="planner-today-btn" onClick={goToToday}>
              Oggi
            </button>

            <button
              type="button"
              className="planner-nav-btn"
              onClick={goToPreviousMonth}
              aria-label="Mese precedente"
            >
              ‹
            </button>

            <button
              type="button"
              className="planner-nav-btn"
              onClick={goToNextMonth}
              aria-label="Mese successivo"
            >
              ›
            </button>
          </div>

          <h3 className="planner-month-title">
            {plannerMonthLabel}
          </h3>
        </div>

        <div className="planner-weekdays">
          {["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"].map((dayName) => (
            <div key={dayName} className="planner-weekday">
              {dayName}
            </div>
          ))}
        </div>

        <div className="planner-grid">
          {plannerDays.map((day) => (
            <div
              key={day.key}
              className={[
                "planner-day",
                !day.currentMonth ? "outside-month" : "",
                day.today ? "today" : "",
                day.selected ? "selected" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              role="button"
              tabIndex={0}
              onClick={() => handleDateClick(day.date)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleDateClick(day.date);
                }
              }}
            >
              <div className="planner-day-number">
                {day.date.getDate()}
              </div>

              <div className="planner-events">
                {day.events.slice(0, 3).map((intervento, index) => (
                  <button
                    key={`${day.key}-${intervento.id || index}`}
                    type="button"
                    className={`planner-event planner-event-${String(
                      intervento.stato || "default"
                    )
                      .toLowerCase()
                      .replace(/\s+/g, "-")}`}
                    title={
                      intervento.cliente ||
                      intervento.descrizione ||
                      "Intervento"
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      handleInterventoClick(intervento);
                    }}
                  >
                    {intervento.cliente ||
                      intervento.descrizione ||
                      "Intervento"}
                  </button>
                ))}

                {day.events.length > 3 && (
                  <button
                    type="button"
                    className="planner-more-events"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDateClick(day.date);
                    }}
                  >
                    +{day.events.length - 3} altri
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {showInterventiList && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-content">
              <h3>Interventi del {date.toLocaleDateString()}</h3>
              {selectedDayInterventi.length > 0 ? (
                <ul className="interventi-list">
                  {selectedDayInterventi.map((intervento, index) => (
                    <li key={index} className="intervento-item">
                      <span className="intervento-info">
                        <strong>{intervento.cliente || "Senza cliente"}</strong> -{" "}
                        {intervento.descrizione || "Senza descrizione"}
                        <span className="intervento-stato" style={{ color: getColorByStato(intervento.stato) }}>
                          ({intervento.stato || "Da fare"})
                        </span>
                      </span>
                      <button className="edit-btn" onClick={() => handleInterventoClick(intervento)}>
                        Modifica
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>Nessun intervento per questo giorno.</p>
              )}
              <button className="add-btn" onClick={handleAddNewIntervento}>
                Aggiungi Nuovo Intervento
              </button>
              <button className="close-btn" onClick={() => setShowInterventiList(false)}>
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendario;




